import React from "react";
import { motion } from "motion/react";
import { Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Music, 
  Users, 
  Star, 
  Moon, 
  Home,
  Sparkles,
  MapPin,
  Phone,
  CheckCircle,
  Play,
  Clock,
  Ticket
} from "lucide-react";
import { Button } from "../components/ui/button";
import { mamuskaClub, WHATSAPP_LINK, WHATSAPP_NUMBER, EMAIL } from "../data/mock";

const iconMap = {
  Music: Music,
  Users: Users,
  Star: Star,
  Moon: Moon,
  Home: Home,
  Sparkles: Sparkles,
};

const MamuskaClubPage = () => {
  const navigate = useNavigate();

  const getWhatsAppUrl = () => {
    const message = `Hello! I'm interested in Mamuska Club events. I'd like information about upcoming parties and VIP reservations.`;
    return `${WHATSAPP_LINK}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] lg:h-[70vh] overflow-hidden">
        <img
          src={mamuskaClub.images[0]}
          alt={mamuskaClub.nameEn}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate("/")}
          className="absolute top-24 left-6 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-gray-800 hover:bg-white transition-colors shadow-lg"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Home</span>
        </motion.button>

        {/* Video Button */}
        <a
          href={mamuskaClub.videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-24 right-6 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-gray-800 hover:bg-white transition-colors shadow-lg"
        >
          <Play className="w-4 h-4 text-[hsl(var(--coral))] fill-current" />
          <span className="text-sm font-medium">Watch Video</span>
        </a>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-12">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="inline-block bg-[hsl(var(--coral))] text-white text-sm font-semibold px-4 py-1 rounded-full mb-4">
                Electronic Music Venue
              </span>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
                {mamuskaClub.nameEn}
              </h1>
              <div className="flex items-center gap-2 text-white/90">
                <MapPin className="w-5 h-5" />
                <span>{mamuskaClub.location}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column - Details */}
            <div className="lg:col-span-2">
              {/* Tagline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-[hsl(var(--coral))] text-xl font-medium mb-6"
              >
                {mamuskaClub.tagline}
              </motion.p>

              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="prose prose-lg max-w-none mb-10"
              >
                <p className="text-gray-600 leading-relaxed text-lg">
                  {mamuskaClub.description}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Whether you're a seasoned raver or experiencing Brazilian nightlife for the first time, 
                  Mamuska Club offers an authentic underground experience that showcases the best of 
                  Rio's vibrant electronic music scene. The venue's unique concept of combining 
                  accommodation with world-class parties makes it a must-visit destination for 
                  music-loving tourists.
                </p>
              </motion.div>

              {/* Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-10"
              >
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">
                  What Makes Mamuska Special
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {mamuskaClub.features.map((feature, index) => {
                    const Icon = iconMap[feature.icon];
                    return (
                      <div
                        key={index}
                        className="flex items-center gap-3 bg-gray-900 p-4 rounded-xl"
                      >
                        <div className="w-10 h-10 rounded-full bg-[hsl(var(--coral))/0.2] flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[hsl(var(--coral))]" />
                        </div>
                        <span className="text-white font-medium text-sm">
                          {feature.textEn}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Event Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mb-10 bg-gradient-to-r from-[hsl(var(--coral))/0.1] to-[hsl(var(--golden))/0.1] p-6 rounded-2xl"
              >
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                  Event Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[hsl(var(--coral))]" />
                    <div>
                      <p className="text-sm text-gray-500">Typical Schedule</p>
                      <p className="font-medium text-gray-900">{mamuskaClub.eventInfo.typical}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Ticket className="w-5 h-5 text-[hsl(var(--coral))]" />
                    <div>
                      <p className="text-sm text-gray-500">Entry</p>
                      <p className="font-medium text-gray-900">{mamuskaClub.eventInfo.entry}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Music className="w-5 h-5 text-[hsl(var(--coral))]" />
                    <div>
                      <p className="text-sm text-gray-500">Music Style</p>
                      <p className="font-medium text-gray-900">{mamuskaClub.eventInfo.vibe}</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-10"
              >
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">
                  Why Visit Mamuska Club
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {mamuskaClub.highlightsEn.map((highlight, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[hsl(var(--whatsapp))] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{highlight}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Video Embed */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-10"
              >
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">
                  Experience the Vibe
                </h2>
                <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                  <iframe
                    src={mamuskaClub.videoEmbed}
                    title="Mamuska Club Video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </motion.div>

              {/* Image Gallery */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">
                  Gallery
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {mamuskaClub.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Mamuska Club - View ${index + 1}`}
                      className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                    />
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="sticky top-24 bg-gray-900 rounded-2xl shadow-xl p-6 text-white"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Music className="w-5 h-5 text-[hsl(var(--coral))]" />
                  <span className="text-[hsl(var(--coral))] font-semibold">Electronic Music</span>
                </div>

                <h3 className="font-display text-xl font-bold mb-2">
                  Plan Your Night Out
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Contact us for upcoming events, VIP reservations, and special packages for tourists.
                </p>

                <div className="space-y-4 mb-6">
                  <motion.a
                    href={getWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="block"
                  >
                    <Button className="w-full bg-[hsl(var(--whatsapp))] hover:bg-[hsl(142_69%_38%)] text-white rounded-full py-4 h-auto font-semibold gap-2">
                      <Phone className="w-5 h-5" />
                      WhatsApp: {WHATSAPP_NUMBER}
                    </Button>
                  </motion.a>

                  <a href={`mailto:${EMAIL}`} className="block">
                    <Button
                      variant="outline"
                      className="w-full rounded-full py-4 h-auto font-medium border-gray-700 text-white hover:bg-gray-800"
                    >
                      Email Us
                    </Button>
                  </a>
                </div>

                <div className="border-t border-gray-700 pt-6">
                  <h4 className="font-semibold mb-3">Location</h4>
                  <p className="text-gray-400 text-sm mb-4">
                    {mamuskaClub.location}
                  </p>
                  <p className="text-gray-500 text-xs">
                    Located in Vargem Pequena, the venue offers a unique escape from 
                    the city while still being accessible from all major tourist areas.
                  </p>
                </div>

                <div className="border-t border-gray-700 pt-6 mt-6">
                  <h4 className="font-semibold mb-3">Tips for Tourists</h4>
                  <ul className="text-gray-400 text-sm space-y-2">
                    <li>• Arrive early for free entry (before midnight)</li>
                    <li>• Book transport in advance (we can help!)</li>
                    <li>• Dress code: casual/club wear</li>
                    <li>• English-friendly staff available</li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/">
            <Button
              variant="outline"
              className="rounded-full px-8 py-3 h-auto font-medium gap-2 border-gray-700 text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to All Services
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MamuskaClubPage;
